import { useForm } from '@mantine/form';
import {
  Button,
  Group,
  LoadingOverlay,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import React, { useEffect } from 'react';

import { useBrandList } from '../../../hooks/brands/useBrandList.tsx';
import { ServiceStatus } from '../../../models/PlaceService.ts';
import { useTagList } from '../../../hooks/tags/useTagList.ts';
import { usePaymentMethodList } from '../../../hooks/payment-methods/usePaymentMethodList.ts';

export interface MutateChargingServiceBasicInformationFormValues {
  name: string;
  serviceStatus: ServiceStatus | null;
  brandId: string;
  phoneNumberCountryCode: string;
  phoneNumberValue: string;
  website: string;
  notes: string;
  tags: string[];
  paymentMethods: string[];
}

interface MutateChargingServiceBasicInformationFormProps {
  initialValues: MutateChargingServiceBasicInformationFormValues;
  onSubmit: (
    formValues: MutateChargingServiceBasicInformationFormValues,
  ) => void;
  onValuesChange?: (
    formValues: MutateChargingServiceBasicInformationFormValues,
  ) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateChargingServiceBasicInformationForm({
  initialValues,
  onSubmit,
  onValuesChange,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateChargingServiceBasicInformationFormProps) {
  const form = useForm<MutateChargingServiceBasicInformationFormValues>({
    mode: 'controlled',
    initialValues: {
      name: initialValues.name,
      serviceStatus: initialValues.serviceStatus,
      brandId: initialValues.brandId,
      phoneNumberCountryCode: initialValues.phoneNumberCountryCode,
      phoneNumberValue: initialValues.phoneNumberValue,
      website: initialValues.website,
      notes: initialValues.notes,
      tags: initialValues.tags,
      paymentMethods: initialValues.paymentMethods,
    },
    validate: {
      name: (value) => {
        if (!value.trim()) return ' ';
      },
      serviceStatus: (value) => {
        if (!value) return ' ';
      },
      phoneNumberCountryCode: (value) => {
        if (!value?.trim() && form.values.phoneNumberValue?.trim()) return ' ';
      },
      phoneNumberValue: (value) => {
        if (!value?.trim() && form.values.phoneNumberCountryCode?.trim())
          return ' ';
      },
      website: (value) => {
        if (value?.trim()) {
          try {
            new URL(value);
          } catch {
            return '無效的 URL 格式';
          }
        }
      },
    },
  });

  useEffect(() => {
    onValuesChange?.(form.values);
  }, [form.values, onValuesChange]);

  const { brands, isLoading: isBrandListLoading } = useBrandList();
  const { data: tags, isLoading: isTagListLoading } = useTagList();
  const { data: paymentMethods, isLoading: isPaymentMethodListLoading } =
    usePaymentMethodList();

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (isBrandListLoading || isTagListLoading || isPaymentMethodListLoading)
    return <LoadingOverlay visible={false} />;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {/* 充電服務名稱 */}
          <Group gap={16}>
            <TextInput
              label={'充電服務名稱'}
              placeholder={'請輸入充電服務名稱'}
              withAsterisk
              key={form.key('name')}
              {...form.getInputProps('name')}
              styles={{
                input: {
                  width: 305.5,
                  borderColor: form.errors.name
                    ? 'var(--mantine-color-red-6)'
                    : undefined,
                },
              }}
            />
            {/* 服務狀態 */}
            <Select
              label={'服務狀態'}
              placeholder={'請選擇服務狀態'}
              withAsterisk
              data={[
                { label: '服務中', value: 'ONLINE' },
                { label: '暫停中', value: 'TEMPORARY_OFFLINE' },
                { label: '停止服務', value: 'DISABLED' },
              ]}
              key={form.key('serviceStatus')}
              {...form.getInputProps('serviceStatus')}
              styles={{
                input: {
                  width: 305.5,
                  borderColor: form.errors.serviceStatus
                    ? 'var(--mantine-color-red-6)'
                    : undefined,
                },
              }}
            />
          </Group>
          <Group gap={16}>
            {/* 品牌名稱 */}
            <Select
              label={'品牌名稱'}
              placeholder={'請選擇品牌名稱'}
              searchable
              data={brands.map((brand) => ({
                label: brand.name,
                value: brand.id,
              }))}
              key={form.key('brandId')}
              {...form.getInputProps('brandId')}
              styles={{
                input: {
                  width: 305.5,
                  borderColor: form.errors.brandId
                    ? 'var(--mantine-color-red-6)'
                    : undefined,
                },
              }}
            />
            <Group gap={0}>
              <Select
                label={'服務電話'}
                placeholder={'請選擇國碼'}
                data={[
                  { label: '🇹🇼 +886', value: '+886' },
                  { label: '🇯🇵 +81', value: '+81' },
                ]}
                key={form.key('phoneNumberCountryCode')}
                {...form.getInputProps('phoneNumberCountryCode')}
                styles={{
                  wrapper: {
                    width: 108,
                  },
                  input: {
                    width: 108,
                    fontSize: 12,
                    borderColor: form.errors.brandId
                      ? 'var(--mantine-color-red-6)'
                      : undefined,
                  },
                  option: {
                    fontSize: 12,
                  },
                }}
              />
              <TextInput
                label={' '}
                placeholder={'請輸入服務電話'}
                key={form.key('phoneNumberValue')}
                {...form.getInputProps('phoneNumberValue')}
                styles={{
                  input: {
                    width: 197.5,
                    borderColor: form.errors.phoneNumberValue
                      ? 'var(--mantine-color-red-6)'
                      : undefined,
                  },
                }}
              />
            </Group>
          </Group>

          {/* 官方網址 */}
          <TextInput
            label={'官方網址'}
            placeholder={'請輸入官方網址'}
            key={form.key('website')}
            {...form.getInputProps('website')}
            styles={{
              input: {
                width: 627,
                borderColor: form.errors.website
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />

          {/* 服務標籤 */}
          <MultiSelect
            label={
              <Group w={627} justify={'space-between'}>
                <Text size={'sm'} fw={500}>
                  服務標籤
                </Text>
                <Button
                  variant={'outline'}
                  onClick={() => window.open('/tags', '_blank')}
                  styles={{
                    root: {
                      width: 60,
                      height: 15,
                      paddingInline: 6,
                    },
                    label: {
                      fontSize: 10,
                    },
                  }}
                >
                  管理標籤
                </Button>
              </Group>
            }
            searchable
            placeholder={'請選擇服務標籤'}
            data={tags?.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            key={form.key('tags')}
            {...form.getInputProps('tags')}
            styles={{
              input: {
                width: 627,
                borderColor: form.errors.tags
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />

          {/* 可用付款方式 */}
          <MultiSelect
            label={
              <Group w={627} justify={'space-between'}>
                <Text size={'sm'} fw={500}>
                  可用付款方式
                </Text>
                <Button
                  variant={'outline'}
                  onClick={() => window.open('/payment-methods', '_blank')}
                  styles={{
                    root: {
                      width: 100,
                      height: 15,
                      paddingInline: 6,
                    },
                    label: {
                      fontSize: 10,
                    },
                  }}
                >
                  管理場站付款方式
                </Button>
              </Group>
            }
            searchable
            placeholder={'請選擇可用付款方式'}
            data={paymentMethods?.map((paymentMethod) => ({
              label: paymentMethod.name,
              value: paymentMethod.id,
            }))}
            key={form.key('paymentMethods')}
            {...form.getInputProps('paymentMethods')}
            styles={{
              input: {
                borderColor: form.errors.paymentMethods
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />

          {/* 備註文字 */}
          <Textarea
            label={'備註文字'}
            placeholder={'請輸入備註文字'}
            rows={4}
            key={form.key('notes')}
            {...form.getInputProps('notes')}
            styles={{
              input: {
                borderColor: form.errors.notes
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </form>
    </>
  );
}
